import * as React from 'react';
import { Fragment, useState } from 'react';
import { Avatar, Box, Button, Divider, List, ListItem, ListItemAvatar, ListItemText, Paper, Typography } from "@mui/material";
import { resources } from "../resources";
import { PaypalPayment } from "./paypal";
import { StripeCardPayment, StripePaymentRequest, StripeSepaPayment } from "./stripe";
import { PaymentType } from "../types";
import { styles } from "../theme";
import { EuroIcon, ImageIcon, LayersIcon, PaymentIcon, RefreshIcon } from "../icons";
import { PayPalIcon } from "../assets/PayPalIcon";
import { WalleePayment } from "./wallee";
import { CPayPayment } from "./cpay";
const messages = resources.getStrings();
export function PaymentMethodList({ paymentMethods, currency, onPaymentError, onPaymentConfirmed, onSelect, payeeName, country, amount, onBusy }) {
    const [disabled, setDisabled] = useState(false);
    const [canPR, setCanPR] = useState(true);
    const hasWalleeGateway = paymentMethods.find(m => !!m.wallee) != null;
    function handleMethodSelection(method, useStoredMethod) {
        setDisabled(true);
        onSelect(method, useStoredMethod);
        window.setTimeout(() => {
            setDisabled(false);
        }, 3000);
    }
    if (hasWalleeGateway) {
        return (React.createElement(WalleePayment, { onLoaded: onBusy }));
    }
    return (React.createElement(Fragment, null,
        React.createElement(Typography, { variant: "body1", align: "center" }, messages.SelectPaymentMethod),
        React.createElement(Box, { my: 2 }),
        React.createElement(Paper, { variant: "outlined", style: { position: 'relative' } },
            disabled && React.createElement("div", { style: { position: 'absolute', zIndex: 9999, width: '100%', height: '100%' } }),
            React.createElement(List, { style: { padding: 0 } }, paymentMethods
                .map((method, i) => {
                var _a, _b;
                return (React.createElement("div", { key: 'payment-method-' + i },
                    i > 0 && React.createElement(Divider, null),
                    (((_a = method === null || method === void 0 ? void 0 : method.stripe) === null || _a === void 0 ? void 0 : _a.last4Digits) !== undefined) && (React.createElement(ListItem, { button: true, disabled: disabled, onClick: () => handleMethodSelection(method, true) },
                        React.createElement(ListItemAvatar, null,
                            React.createElement(Avatar, { style: styles.primaryBg },
                                React.createElement(RefreshIcon, { style: { color: '#fff' } }))),
                        React.createElement(ListItemText, { primary: messages['StorePaymentMethod' + method.stripe.type], secondary: (_b = method.stripe) === null || _b === void 0 ? void 0 : _b.last4Digits }))),
                    (method.stripe && method.stripe.last4Digits === undefined && [PaymentType.Cards, PaymentType.SepaDirectDebit].indexOf(method.stripe.type) > -1) && (React.createElement(ListItem, { button: true, disabled: disabled, onClick: () => handleMethodSelection(method, false) },
                        React.createElement(ListItemAvatar, null,
                            React.createElement(Avatar, { style: styles.primaryBg }, renderPaymentTypeIcon(method.stripe.type))),
                        React.createElement(ListItemText, { primary: messages['PaymentMethod' + method.stripe.type], secondary: messages['PaymentMethod' + method.stripe.type + 'Description'] }))),
                    (method.stripe && method.publicKey && method.stripe.type === PaymentType.PaymentRequest && canPR) && (React.createElement(ListItem, { disabled: disabled },
                        React.createElement(ListItemAvatar, null,
                            React.createElement(Avatar, { style: styles.primaryBg }, renderPaymentTypeIcon(PaymentType.PaymentRequest))),
                        React.createElement(StripePaymentRequest, { publicKey: method.publicKey, currency: currency, amount: amount, country: country, payeeName: payeeName, onCanceled: () => setDisabled(false), onSelected: () => handleMethodSelection(method, false), onPaymentError: e => onPaymentError(e), onPaymentConfirmed: status => onPaymentConfirmed(status, method), onNotSupported: () => setCanPR(false) }))),
                    (method.paypal && method.publicKey !== undefined) && (React.createElement(ListItem, { disabled: disabled },
                        React.createElement(ListItemAvatar, null,
                            React.createElement(Avatar, { style: styles.primaryBg }, renderPaymentTypeIcon(PaymentType.PayPal))),
                        React.createElement(PaypalPayment, { publicKey: method.publicKey, currency: currency, onCanceled: () => setDisabled(false), onSelected: () => handleMethodSelection(method, false), onError: onPaymentError, onPaymentConfirmed: status => onPaymentConfirmed(status, method) }))),
                    method.cpay && (React.createElement(ListItem, { disabled: disabled },
                        React.createElement(ListItemAvatar, null,
                            React.createElement(Avatar, { style: styles.primaryBg }, renderPaymentTypeIcon(PaymentType.Cards))),
                        React.createElement(CPayPayment, null)))));
            })))));
}
function renderPaymentTypeIcon(type) {
    switch (type) {
        case PaymentType.Cards:
            return React.createElement(PaymentIcon, { style: { color: '#fff' } });
        case PaymentType.SepaDirectDebit:
            return React.createElement(EuroIcon, { style: { color: '#fff' } });
        case PaymentType.PayPal:
            return React.createElement(PayPalIcon, { style: { height: '50%', overflow: 'hidden' } });
        case PaymentType.PaymentRequest:
            return React.createElement(LayersIcon, { style: { color: '#fff' } });
        default:
            return React.createElement(ImageIcon, { style: { color: '#fff' } });
    }
}
export function PaymentView(props) {
    const { payeeName, paymentMethod, onPaymentBack, onPaymentError, onPaymentConfirmed, stripeClientSecret } = props;
    let method;
    if (paymentMethod.stripe && paymentMethod.publicKey && stripeClientSecret) {
        switch (paymentMethod.stripe.type) {
            case PaymentType.Cards:
                method = React.createElement(StripeCardPayment, { stripeClientSecret: stripeClientSecret, onPaymentError: e => onPaymentError(new Error('Failed to make Stripe Card Payment, error: ' + e.message)), onPaymentConfirmed: (status, saveMethod) => onPaymentConfirmed(status, paymentMethod, saveMethod), publicKey: paymentMethod.publicKey });
                break;
            case PaymentType.SepaDirectDebit:
                method = React.createElement(StripeSepaPayment, { payeeName: payeeName, stripeClientSecret: stripeClientSecret, onPaymentError: e => onPaymentError(new Error('Failed to make Stripe Sepa Payment, error: ' + e.message)), onPaymentConfirmed: (status, saveMethod) => onPaymentConfirmed(status, paymentMethod, saveMethod), publicKey: paymentMethod.publicKey });
                break;
            default:
                method = renderPaymentTypeNotSupported();
                break;
        }
    }
    return (React.createElement("div", null,
        method,
        React.createElement(Box, { mt: 2, style: { textAlign: 'center' } },
            React.createElement(Button, { onClick: () => onPaymentBack() }, messages.Back))));
}
function renderPaymentTypeNotSupported() {
    return React.createElement(Typography, { variant: "body1", align: "center" }, messages.PaymentMethodNotSupported);
}
//# sourceMappingURL=payment.js.map